/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {useCallback, useState} from 'react';
import {useAgentsAPI} from './useAgentsAPI';
import {METHODS} from '../../../hooks/abstractions/useTrusstedAgent';
import {type Agent as AgentLocal} from '../../../types/agent';
import {AGENTS_API_EVENT_NAMES} from '../../../types/agents-api';
import {mapAgentRemoteToLocal} from '../utils';

export const useGetAgent = () => {
  const agentsAPI = useAgentsAPI();

  const [agent, setAgent] = useState<AgentLocal | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(
    async (agentId: string) => {
      setIsLoading(true);
      setError(null);

      try {
        const queryParams = {agentId};
        const result = await agentsAPI({
          eventName: AGENTS_API_EVENT_NAMES.GetAgent,
          methodName: METHODS.GET,
          params: {},
          queryParams,
        });

        const remappedAgent = mapAgentRemoteToLocal(result.agent);
        setAgent(remappedAgent);
      } catch (err) {
        setError('Error fetching the agent');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    },
    [agentsAPI],
  );

  return {
    agent,
    error,
    fetch,
    isLoading,
  };
};
